import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { SmartFilterDataType } from '@Portal/models/smartFilters/SmartFilterDataType';
import { getFilterData } from '@Shared/api/portal';
import { generalisedMetricLabel } from '@Shared/components/visuals/utility';
import { BLANK_VALUE } from '@Shared/constants';
import { useRootStore } from '@Shared/stores/root';
import { FrontlineFiltersStoreState } from '@Shared/types/FrontlineFiltersStoreState';
import { ProcessFlowTopicFilter } from '@Shared/types/ProcessFlowTopicFilter';
import { FrontlineDashboardDataSource } from '@Shared/types/portal/dashboard';
import { trackAppInsightsEvent, useAppInsights } from '@Shared/utils/util';
import { defineStore } from 'pinia';

// just some convenience functions to map arrays of filter values to arrays of filters
const getSimpleSmartFilterMapper = (fieldKey: string) => (value: string | number) =>
  new SmartFilter(fieldKey, SmartFilterDataType.String, String(value));

export const useFrontlineFiltersStore = defineStore('frontlineFilters', {
  state: (): FrontlineFiltersStoreState => ({
    isLoading: false,
    // *don't* use this in the getters unless you are using it to get a list of possible filters
    // else it'll cause reloads instead use the cache object { filterKeys }
    possibleFilters: null,
    filterKeys: null,
    metricValuesSelected: [],
    claimsSelected: [],
    topicsSelected: [],
    workoutDate: null,
    workoutDateFilterKey: null,
    selectedProgramFilter: null, // used only in the popup modal when changing between programs
  }),
  getters: {
    // these only apply to top banner
    topBannerFilters(): SmartFilter[] {
      // we use no filters to fetch the top banner details
      return [];
    },
    // these apply to *all* visuals
    globalFilters(): SmartFilter[] {
      return this.filterKeys ? this.claimsSelected.map(getSimpleSmartFilterMapper(this.filterKeys.Claims)) : [];
    },
    // these are for the workout scorecard on the left
    workoutCardFilters(): SmartFilter[] {
      const filters = [...this.globalFilters];
      if (this.workoutDate) filters.push(this.workoutDate);
      return filters;
    },
    // these are for the processflow feed on the right
    processflowFilters(): SmartFilter[] {
      if (!this.filterKeys) return [];

      return [
        ...this.globalFilters,
        ...this.metricValuesSelected.map(getSimpleSmartFilterMapper(this.filterKeys.PrimaryMetric)),
        ...this.topicsSelected
          .map((topic) => topic.label)
          .map(getSimpleSmartFilterMapper(this.filterKeys.TextAnalyticsTopics)),
      ];
    },
    // useful UI getters
    // these can / should use possibleFilters (since they are showing what filters can be used)
    processFlowTopicsToShow(): ProcessFlowTopicFilter[] {
      return (
        (this.possibleFilters?.TextAnalyticsTopics.filterDimensionValues ?? [])
          .map((val) => ({ label: val.dimensionValue, count: val.count }))
          .filter(
            (val) =>
              // ignore all the selected filters (we'll add them afterwards)
              // this is because the backend doesn't return them (but demos often do for simplicity)
              !this.topicsSelected.some((topic) => topic.label == val.label) && val.label != BLANK_VALUE
          )
          .sort((a, b) =>
            // order is count > alphabetical (so filters don't just randomly move around)
            b.count == a.count ? a.label.localeCompare(b.label) : b.count - a.count
          )
          // let's not introduce *new* filters in (since the backend will be top 10 not including selected filters)
          .slice(0, 10 - this.topicsSelected.length)
          // we also want to add all the selected ones!
          .concat(this.topicsSelected)
          // we have to obviously re-sort by the count
          // (since the concatenation isn't going to be in the right order)
          .sort((a, b) =>
            // order is count > alphabetical (so filters don't just randomly move around)
            b.count == a.count ? a.label.localeCompare(b.label) : b.count - a.count
          )
      );
    },
    primaryMetricChoices(): {
      value: string;
      isSelected: boolean;
      isActive: boolean;
    }[] {
      return this.possibleFilters
        ? this.possibleFilters.PrimaryMetric.filterDimensionValues
            // we'll concatenate the selected values after
            .filter(
              ({ dimensionValue }) =>
                // this is because the backend doesn't return them (but demos often do for simplicity)
                !this.metricValuesSelected.includes(dimensionValue)
            )
            .map(({ dimensionValue }) => ({
              value: dimensionValue,
              // since we filtered out the selected ones
              isSelected: false,
              // this can only be active if we have nothing selected
              // (since nothing selected is equivalent to everything)
              isActive: this.metricValuesSelected.length == 0,
            }))
            .concat(
              this.metricValuesSelected.map((x) => ({
                value: x.toString(),
                isSelected: true,
                isActive: true,
              }))
            )
            // sort value desc (i.e. 1, 0, -1, [Blank])
            .sort(function (a, b) {
              if (a.value == '[Blank]') {
                return 1;
              } else if (b.value == '[Blank]') {
                return -1;
              }
              if (b.value < a.value) {
                return -1;
              } else {
                return 1;
              }
            })
        : // TODO: fix sort
          [];
    },
  },
  actions: {
    initialise(dataSource: FrontlineDashboardDataSource) {
      const workoutDate = dataSource.defaultSmartFilters.find(
        (sm) =>
          sm.filterDataType === SmartFilterDataType.DateTime || sm.filterDataType === SmartFilterDataType.DateTimePeriod
      );
      if (!workoutDate) return;

      this.$patch({
        workoutDate,
        workoutDateFilterKey: workoutDate.filterKey,
        filterKeys: dataSource.filterKeys,
      });

      this.loadFilters();
    },
    isTopicSelected(filter: ProcessFlowTopicFilter) {
      return this.topicsSelected.some((topic) => topic.label == filter.label);
    },
    setClaimsSelected(updatedClaimValues: string[]) {
      if (!this.filterKeys) return;

      let status: string;
      if (updatedClaimValues.length == 0) status = 'Removed';
      else if (this.claimsSelected.length == 0) status = 'Added';
      else status = 'Set';

      this.trackEvent('Filter', {
        type: 'Claims',
        values: updatedClaimValues.join(','),
        status,
        // this isn't exactly correct (technically they are separate filters)
        // but it's fine for this case.
        filter: JSON.stringify(getSimpleSmartFilterMapper(this.filterKeys.Claims)(updatedClaimValues.join(','))),
      });

      this.claimsSelected = updatedClaimValues;
    },
    toggleProcessFlowPrimaryMetric(value: string) {
      if (!this.possibleFilters) return;
      if (!this.filterKeys) return;

      // was this set or removed
      let status: string;

      if (this.metricValuesSelected.includes(value)) {
        // removed
        status = 'Removed';

        // toggle off
        // so it doesn't disappear in the UI we'll add it to the list of possible filters.
        // we do check that it's not in the list already (since in demos the list of filters doesn't change like portal
        // and is static)
        if (
          !this.possibleFilters.PrimaryMetric.filterDimensionValues.some((filter) => filter.dimensionValue == value)
        ) {
          this.possibleFilters.PrimaryMetric.filterDimensionValues.push({
            dimensionValue: value,
            count: 0,
          });
        }
        this.metricValuesSelected = this.metricValuesSelected.filter((selectedValue) => selectedValue != value);
      } else {
        // wasn't set previously
        status = 'Added';

        // toggle on
        // determine if toggling this value on would result in all possible filters being on
        const newFilters = [...this.metricValuesSelected, value];
        const allFiltersWouldBeToggledOn = this.possibleFilters.PrimaryMetric.filterDimensionValues
          .map((value) => value.dimensionValue)
          .every(
            // either the possibility is already set *or* it's our value
            // this is just to cover the possible case where you've selected one then changed
            // another filter elsewhere reducing the set of possibilities so that a simple length
            // check is no longer possible.
            (value) => newFilters.includes(value)
          );
        if (allFiltersWouldBeToggledOn) {
          // if we have 0 values selected, and selecting one value selects all possible values
          // then there is only one option so we'll basically just not filter by it.
          if (this.metricValuesSelected.length > 0) {
            // reset filters
            // make sure to update the filter dimension values (possible filters)
            // so that the UI doesn't just disappear the options while we are waiting for the backend
            // to tell us what filters are possible (we can presume since they were selected in the first place
            // the backend should return them).
            this.possibleFilters.PrimaryMetric.filterDimensionValues = newFilters.map((filter) => ({
              dimensionValue: filter,
              count: 0,
            }));
            this.metricValuesSelected = [];
          }
        } else {
          // add filter
          this.metricValuesSelected = newFilters;
        }
      }

      this.trackEvent('Filter', {
        type: 'PrimaryMetric',
        value: String(value),
        label: generalisedMetricLabel(value),
        status, // Added/Removed (and "Set" but not relevant in this case since it either is set or isn't)
        filter: JSON.stringify(getSimpleSmartFilterMapper(this.filterKeys.PrimaryMetric)(value)),
      });
    },
    toggleTopic(value: ProcessFlowTopicFilter) {
      if (!this.possibleFilters) return;
      if (!this.filterKeys) return;

      let status: string;

      if (this.topicsSelected.some((filter) => filter.label == value.label)) {
        status = 'Removed';

        // make sure to add this topic to the list of possible filters (same logic as with nps above)
        if (
          !this.possibleFilters.TextAnalyticsTopics.filterDimensionValues.some((x) => x.dimensionValue == value.label)
        ) {
          this.possibleFilters.TextAnalyticsTopics.filterDimensionValues.push({
            dimensionValue: value.label,
            count: value.count,
          });
        }

        this.topicsSelected = this.topicsSelected.filter((topic) => topic.label != value.label);
      } else {
        status = 'Added';
        this.topicsSelected = [...this.topicsSelected, value];
      }

      this.trackEvent('Filter', {
        type: 'TextAnalyticsTopics',
        count: String(value.count),
        label: value.label,
        status, // Added/Removed (and "Set" but not relevant in this case since it either is set or isn't)
        filter: JSON.stringify(getSimpleSmartFilterMapper(this.filterKeys.TextAnalyticsTopics)(value.label)),
      });
    },
    clearProcessflowTopicFilters() {
      this.topicsSelected = [];
    },
    setDateFilter(filter: SmartFilter | null) {
      this.workoutDate = filter;
    },
    selectProgramFilter(program: string) {
      this.selectedProgramFilter = program;
    },
    async loadFilters() {
      // this is a UI bug, it shouldn't allow us to change filters while we are loading
      if (this.isLoading) {
        useAppInsights().trackException({
          exception: new Error('Attempt to change filters while we were loading filters already, this is a UI bug.'),
        });
        return;
      }

      const root = useRootStore();
      try {
        // don't await this since we want to run both in parallel
        const processFlowResponse = getFilterData(root.moduleId!, 'ProcessFlowFeed', this.processflowFilters);
        // we don't need to refetch it if we already have loaded claims
        const topBannerResponse =
          this.possibleFilters?.Claims ||
          (
            await getFilterData(
              root.moduleId!,
              'TopBanner',
              // note: we don't want to add filters here
              []
            )
          ).filters['Claims'];

        this.possibleFilters = {
          Claims: topBannerResponse,
          PrimaryMetric: (await processFlowResponse).filters['PrimaryMetric'],
          TextAnalyticsTopics: (await processFlowResponse).filters['TextAnalyticsTopics'],
        };
        this.isLoading = false;
      } catch (e) {
        useAppInsights().trackException({
          exception: new Error(String(e)),
        });
      } finally {
        this.isLoading = false;
      }
    },
    trackEvent(name: string, props: Record<string, string | undefined>) {
      trackAppInsightsEvent(name, props, 'Frontline', 'FrontlineDashboard');
    },
  },
  persist: {
    paths: ['workoutDate', 'workoutDateFilterKey'],
    storage: sessionStorage,
  },
});
